import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../styles/popup-styles.css';

import {
  Map,
  MapRef,
  NavigationControl,
  Popup,
  ScaleControl,
  ViewStateChangeEvent,
} from 'react-map-gl';

import { Button, Flex, Progress } from '@chakra-ui/react';
import { PublicListingFragment } from '@client/graphql/__generated__/types';
import debounce from 'lodash/debounce';
import { MapEvent } from 'mapbox-gl';
import { decode, encode } from 'ngeohash';
import { RefObject, useCallback, useMemo } from 'react';
import {
  GEO_HASH_PRECISION,
  getIsUserListing,
  getListingPrice,
  groupListingsByGeo,
} from '~/apps/consumer/utils/listing.utils';
import { bboxToLngLatBounds } from '~/apps/consumer/utils/map.utils';
import { useProdFeatureFlagEnabled } from '~/common/hooks/useProdFeatureFlagEnabled';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { GroupedListingCard } from '../../Listings/ListingCard/GroupedListingCard/GroupedListingCard';
import { PropertyDetailsCard } from '../../Listings/ListingCard/PropertyDetailsCard';
import { PublicListingCard } from '../../Listings/ListingCard/PublicListingCard';
import { AddOffMarketListingPrompt } from '../AddOffMarketListingPrompt';
import { DEFAULT_POSITION, MLS_TO_BBOX_MAP } from '../constants';
import { useExploreContext } from '../ExploreContext';
import { convertBoundsToBbox } from '../helpers';
import { useMapCardActions } from '../hooks/useMapCardActions';
import { ExploreMapMarker } from './ExploreMapMarker';

interface IExploreMapProps {
  mapRef: RefObject<MapRef>;
  visibility?: 'visible' | 'hidden';
}

export const ExploreMap = ({
  mapRef,
  visibility = 'visible',
}: IExploreMapProps) => {
  const isOffMarketListingsEnabled = useProdFeatureFlagEnabled(
    'off-market-listings'
  );
  const {
    selectedListing,
    setSelectedListing,
    listings,
    updateFilters,
    loading,
    utmParams,
    foundPropertyDetails,
    setFoundPropertyDetails,
    shouldPromptForOffMarketListing,
    setShouldPromptForOffMarketListing,
    searchResult,
  } = useExploreContext();
  const { user } = useAppContext();
  const { onCardClick, onStartOfferScorecardClick, isCreatingBOA } =
    useMapCardActions({
      isEmbedded: false,
      utmParams,
    });

  const mlsOSN = user?.licenses?.[0]?.mls?.originatingSystemName;

  const markers = useMemo(() => {
    const groupedListings = groupListingsByGeo(listings);

    return Object.entries(groupedListings).map(([geoHash, group]) => {
      if (group.length === 0) {
        return null;
      }

      if (group.length === 1) {
        const { mlsListing, listing } = group[0];

        if (!mlsListing.geo?.location?.lat || !mlsListing.geo?.location?.lon) {
          return null;
        }

        const isUserListing = getIsUserListing(
          listing as PublicListingFragment | undefined,
          user
        );

        const price = getListingPrice(mlsListing);

        return (
          <ExploreMapMarker
            key={`marker-${mlsListing.id}`}
            isPremium={isUserListing}
            lat={mlsListing.geo.location.lat}
            lng={mlsListing.geo.location.lon}
            price={price}
            zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
            onClick={(event) => {
              event.originalEvent.stopPropagation();
              setSelectedListing([
                {
                  listing,
                  mlsListing,
                },
              ]);
            }}
          />
        );
      }

      const latLng = decode(geoHash);

      const lat = latLng.latitude;
      const lng = latLng.longitude;

      return (
        <ExploreMapMarker
          key={`marker-${group
            .map(({ mlsListing }) => mlsListing.id)
            .join('-')}`}
          isGrouped
          bgOverride="gray.500"
          colorOverride="white"
          groupCount={group.length}
          lat={lat}
          lng={lng}
          zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
          onClick={(event) => {
            event.originalEvent.stopPropagation();
            setSelectedListing(group);
          }}
        />
      );
    });
  }, [listings, setSelectedListing, mapRef, user]);

  const fetchListings = useCallback(
    (event: MapEvent) => {
      const bounds = event.target.getBounds();
      const bbox = convertBoundsToBbox(bounds);

      if (bbox) {
        updateFilters({
          bbox,
        });
      }
    },
    [updateFilters]
  );

  const handleMove = useMemo(
    () =>
      debounce((event: ViewStateChangeEvent) => {
        fetchListings(event);
      }, 300),
    [fetchListings]
  );

  const popup = () => {
    if (!selectedListing?.length) {
      return null;
    }

    if (selectedListing?.length === 1) {
      return (
        <Popup
          latitude={selectedListing[0].mlsListing.geo.location.lat}
          longitude={selectedListing[0].mlsListing.geo.location.lon}
          offset={15}
          style={{
            maxWidth: '260px',
            minWidth: '260px',
            zIndex: 2,
            borderRadius: '12px',
          }}
          onClose={() => setSelectedListing([])}
        >
          <PublicListingCard
            showHorizontalMobileCard
            listing={selectedListing[0].listing}
            mlsListing={selectedListing[0].mlsListing}
            utmParams={utmParams}
          />
        </Popup>
      );
    }

    if ((selectedListing?.length || 0) > 1) {
      const hashLatLng = decode(
        encode(
          selectedListing[0].mlsListing.geo.location.lat,
          selectedListing[0].mlsListing.geo.location.lon,
          GEO_HASH_PRECISION
        )
      );

      return (
        <Popup
          latitude={hashLatLng.latitude}
          longitude={hashLatLng.longitude}
          offset={15}
          style={{
            minWidth: '300px',
            maxWidth: '350px',
            zIndex: 2,
            borderRadius: '12px',
          }}
          onClose={() => setSelectedListing([])}
        >
          <GroupedListingCard
            listings={selectedListing}
            rightButton={(listing) => (
              <Flex direction="column" gap={1}>
                <Button size="xs" onClick={() => onCardClick(listing)}>
                  Listing details
                </Button>
                <Button
                  borderColor="indigo.900"
                  color="indigo.900"
                  isLoading={isCreatingBOA}
                  size="xs"
                  variant="outline"
                  onClick={() => void onStartOfferScorecardClick(listing)}
                >
                  Offer Scorecard
                </Button>
              </Flex>
            )}
          />
        </Popup>
      );
    }

    if (isOffMarketListingsEnabled) {
      if (shouldPromptForOffMarketListing && searchResult) {
        return (
          <Popup
            latitude={searchResult.geometry.coordinates[1]}
            longitude={searchResult.geometry.coordinates[0]}
            offset={15}
            style={{
              maxWidth: '260px',
              minWidth: '260px',
              zIndex: 2,
              borderRadius: '12px',
            }}
            onClose={() => setShouldPromptForOffMarketListing(false)}
          >
            <AddOffMarketListingPrompt initialGeocode={searchResult} />
          </Popup>
        );
      }
      if (foundPropertyDetails) {
        return (
          <Popup
            latitude={foundPropertyDetails.geo.lat}
            longitude={foundPropertyDetails.geo.lng}
            offset={15}
            style={{
              maxWidth: '260px',
              minWidth: '260px',
              zIndex: 2,
              borderRadius: '12px',
            }}
            onClose={() => setFoundPropertyDetails(null)}
          >
            <PropertyDetailsCard
              showButton
              propertyDetails={foundPropertyDetails}
            />
          </Popup>
        );
      }
    }

    return null;
  };

  return (
    <Flex
      direction={{ base: 'column-reverse', xl: 'column' }}
      height={{
        base: visibility === 'visible' ? '100%' : '0',
        xl: '100%',
      }}
      visibility={visibility}
      width="100%"
    >
      <Progress
        isIndeterminate
        height="2px"
        size="xs"
        visibility={loading ? 'visible' : 'hidden'}
        width="100%"
      />
      <Map
        ref={mapRef}
        initialViewState={{
          bounds: bboxToLngLatBounds(
            mlsOSN && MLS_TO_BBOX_MAP[mlsOSN]
              ? MLS_TO_BBOX_MAP[mlsOSN]
              : DEFAULT_POSITION.bbox
          ),
        }}
        mapStyle="mapbox://styles/mapbox/navigation-guidance-night-v4"
        mapboxAccessToken={window.ENVS.mapboxApiKey}
        minZoom={3}
        testMode={window.ENVS.environment === 'e2e'}
        onLoad={fetchListings}
        onMove={handleMove}
      >
        <NavigationControl position="bottom-right" />
        <ScaleControl />
        {markers}
        {popup()}
      </Map>
    </Flex>
  );
};
